import { chapterNumber } from "../lib/timeline/book/ChapterId";
import {
  DEFAULT_SCENE_DURATION_MS,
} from "./compile-chapter";
import { withoutFlashbackSelves } from "./flashback-selves";
import { mergeCharacters, mergePlaces } from "./join-chapter-cast";
import { placePath } from "./place-path";
import { railLayoutForScenes, storyOrderScene } from "./rail-branches";
import type { CompiledChapter, TimelineEvent } from "./schema/compiled";
import type { Place } from "./schema/place";
import type { Scene } from "./schema/scene";
import { sortScenesForUniverse } from "./universe-scene-order";
import { clockMs } from "./universe-clock";
import { universeTimeMarks } from "./universe-time-marks";
import { presentCharacterIds } from "./validate-chapter-source";

export const UNIVERSE_CHAPTER_ID = "universe";

/** Book-wide universe rail from every compiled chapter's scenes. */
export function compileBookUniverse(
  chapters: readonly CompiledChapter[],
  sceneDurationMs = DEFAULT_SCENE_DURATION_MS,
  options: { oneRail?: boolean } = {},
): CompiledChapter {
  if (chapters.length === 0) {
    throw new Error("No chapters to compile for universe time.");
  }
  const ordered = [...chapters].sort(byChapter);
  const ranked = sortScenesForUniverse(
    withoutFlashbackSelves(ordered.flatMap((item) => item.scenes)),
  );
  const places = mergePlaces(ordered);
  const events = eventsFor(ranked, places, sceneDurationMs);
  const rail = railLayoutForScenes(
    ranked.map((scene, index) => ({
      ...storyOrderScene(scene),
      inUniverseOrder: index + 1,
    })),
    places,
    sceneDurationMs,
    (scene) => clockMs(scene.narrativeTime?.approxStart ?? "") ?? undefined,
    options,
  );
  return {
    bookId: ordered[0].bookId,
    chapterId: UNIVERSE_CHAPTER_ID,
    title: "Universe time",
    chapterTitles: ordered.map((chapter) => ({
      chapterId: chapter.chapterId,
      title: chapter.title,
    })),
    characters: mergeCharacters(ordered).filter((item) => item.flashbackOf === undefined),
    places,
    scenes: ranked,
    eventsByJourney: { book: events, universe: events },
    railsByJourney: { book: rail, universe: rail },
    timeMarks: universeTimeMarks(ranked, sceneDurationMs),
  };
}

function eventsFor(
  ranked: readonly Scene[],
  places: readonly Place[],
  durationMs: number,
): TimelineEvent[] {
  return ranked.map((scene, index) => {
    const locationPath = scene.placeId === undefined
      ? [scene.location.label]
      : placePath(places, scene.placeId);
    return {
      eventId: `${UNIVERSE_CHAPTER_ID}-universe-${scene.id}`,
      sceneId: scene.id,
      playbackMs: index * durationMs,
      durationMs,
      characterIds: presentCharacterIds(scene),
      locationLabel: locationPath[0] ?? scene.location.label,
      locationPath,
      title: scene.title,
      universeRank: index + 1,
    };
  });
}

function byChapter(a: CompiledChapter, b: CompiledChapter): number {
  return (chapterNumber(a.chapterId) ?? 0) - (chapterNumber(b.chapterId) ?? 0);
}
